import React, {
    MutableRefObject,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { ScheduleTemplateStatus } from "@common/types/domains/accounting/schedule";
import { useScheduleTemplates } from "@api/scheduleTemplate.api";
import { BaseCellProps } from "@components/spreadsheet/cells";
import { Picker } from "@design-system/Picker/Picker.lib";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { ScheduleTemplateListItem } from "@common/schemas/domains/accounting/scheduleTemplate.schema";
import { useScope } from "@state/scopeContext.lib";
import {
    resolveSplitScheduleHolding,
    SplitScheduleHoldingPatch,
} from "./resolveSplitScheduleHolding";

interface Props extends BaseCellProps<string> {
    entityId?: number;
    schedulePatchByRowIdxRef: MutableRefObject<
        Map<number, SplitScheduleHoldingPatch>
    >;
}

function templatesForEntity(
    templates: ScheduleTemplateListItem[],
    entityId: number | undefined,
): ScheduleTemplateListItem[] {
    const activeTemplates = templates.filter(
        (template) => template.status === ScheduleTemplateStatus.active,
    );
    if (entityId === undefined) {
        return activeTemplates;
    }
    return activeTemplates.filter(
        (template) =>
            template.entityIds.length === 0 ||
            template.entityIds.includes(entityId),
    );
}

export const SplitRuleScheduleCell: React.FC<Props> = ({
    value,
    isEditing,
    finishEditing,
    entityId,
    rowIdx,
    schedulePatchByRowIdxRef,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const selectionMadeRef = useRef(false);
    const { workspaceId } = useScope();
    const { data: templates = [] } = useScheduleTemplates();

    const availableTemplates = useMemo(
        () => templatesForEntity(templates, entityId),
        [templates, entityId],
    );

    const templateItems = useMemo(
        () =>
            availableTemplates.map((template) => ({
                value: template.id,
                label: template.name,
            })),
        [availableTemplates],
    );

    const selectedTemplate = useMemo(
        () => templates.find((template) => template.id === value),
        [templates, value],
    );

    const applyScheduleSelection = useCallback(
        async (template: ScheduleTemplateListItem | null) => {
            if (!template) {
                finishEditing(null);
                return;
            }

            try {
                const patch = await resolveSplitScheduleHolding({
                    template,
                    entityId,
                    workspaceId,
                });
                schedulePatchByRowIdxRef.current.set(rowIdx, patch);
            } catch {
                schedulePatchByRowIdxRef.current.delete(rowIdx);
            }
            finishEditing(template.id);
        },
        [
            entityId,
            finishEditing,
            rowIdx,
            schedulePatchByRowIdxRef,
            workspaceId,
        ],
    );

    useEffect(() => {
        if (isEditing) {
            setIsOpen(true);
        }
    }, [isEditing]);

    if (isEditing) {
        return (
            <div
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                    if (["Enter", "Escape"].includes(e.key)) {
                        finishEditing();
                    }
                }}
            >
                <Picker.Select
                    items={templateItems}
                    searchable
                    value={selectedTemplate?.id ?? null}
                    open={isOpen}
                    onValueChange={(templateId) => {
                        selectionMadeRef.current = true;
                        void applyScheduleSelection(
                            templates.find(
                                (template) => template.id === templateId,
                            ) ?? null,
                        );
                    }}
                    onOpenChange={(open) => {
                        setIsOpen(open);
                        if (!open && !selectionMadeRef.current) {
                            finishEditing();
                        }
                        selectionMadeRef.current = false;
                    }}
                    renderValue={() => selectedTemplate?.name}
                    renderFooter={() =>
                        selectedTemplate ? (
                            <Picker.Item
                                onSelect={() => {
                                    selectionMadeRef.current = true;
                                    void applyScheduleSelection(null);
                                }}
                            >
                                Clear selection
                            </Picker.Item>
                        ) : null
                    }
                    chevron="none"
                    placeholder="Select schedule..."
                    searchPlaceholder="Search policies..."
                    emptyLabel="No policies"
                />
            </div>
        );
    }

    if (!selectedTemplate) {
        return null;
    }

    return <BreakableText text={selectedTemplate.name} showTooltip />;
};
